/**
 * Custom: Formular-Baukasten (sections/custom-form.liquid).
 * - Darstellung: auf der Seite, Pop-up in der Ecke oder Pop-up in der Mitte
 * - Mehrstufig über den Block „Formular: Neuer Schritt“
 * - Auslöser: Verzögerung, Inaktivität, Verlassen der Seite, Scrolltiefe, Link/Button (#anker)
 * - Versand als JSON (text/plain, ohne Preflight) an ein Google Apps Script, das in ein Google Sheet schreibt
 */

import { getScrollTop, getScrollContainer } from '@theme/scroll-container';

const STORAGE_PREFIX = 'customForm:';
let openPopup = null;

/**
 * Warteschlange für Einsendungen (localStorage). Eine Antwort wird erst entfernt, wenn das Google-Script
 * den Empfang bestätigt hat. Schlägt das Senden fehl (offline, Seite verlassen …), wird es später und beim
 * nächsten Seitenaufruf erneut versucht. Doppelte Zeilen verhindert das Script über die ID.
 */
const outbox = {
  key: `${STORAGE_PREFIX}outbox`,
  maxAge: 14 * 86400000,
  inFlight: new Set(),

  read() {
    try {
      const items = JSON.parse(localStorage.getItem(this.key) || '[]');
      return Array.isArray(items) ? items : [];
    } catch {
      return [];
    }
  },
  write(items) {
    try {
      localStorage.setItem(this.key, JSON.stringify(items));
    } catch {
      /* Speicher nicht verfügbar: Senden läuft trotzdem */
    }
  },
  newId() {
    return window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  },
  add(endpoint, payload) {
    const entry = { endpoint, payload, created: Date.now(), attempts: 0 };
    this.memory.set(payload.id, entry);
    this.write([...this.read(), entry]);
    return entry;
  },
  update(id, change) {
    const memoryEntry = this.memory.get(id);
    if (memoryEntry) change(memoryEntry);
    const items = this.read();
    const item = items.find((i) => i.payload.id === id);
    if (item) {
      change(item);
      this.write(items);
    }
  },
  remove(id) {
    this.memory.delete(id);
    this.write(this.read().filter((i) => i.payload.id !== id));
  },
  memory: new Map(),

  async send(id, retry = 0) {
    if (this.inFlight.has(id)) return;
    const entry = this.read().find((i) => i.payload.id === id) || this.memory.get(id);
    if (!entry) return;
    this.inFlight.add(id);
    let delivered = false;
    try {
      const response = await fetch(entry.endpoint, {
        method: 'POST',
        body: JSON.stringify(entry.payload),
        keepalive: true, // läuft weiter, auch wenn die Seite gerade verlassen wird
      });
      const result = await response.json().catch(() => null);
      delivered = response.ok && result?.ok === true;
    } catch {
      delivered = false;
    }
    this.inFlight.delete(id);

    if (delivered) {
      this.remove(id);
      return;
    }
    this.update(id, (item) => (item.attempts = (item.attempts || 0) + 1));
    // Im selben Seitenaufruf noch zweimal versuchen (nach 3 s und 15 s), danach beim nächsten Seitenaufruf
    if (retry < 2) setTimeout(() => this.send(id, retry + 1), retry === 0 ? 3000 : 15000);
  },

  flush() {
    const now = Date.now();
    const items = this.read().filter((i) => now - i.created < this.maxAge);
    this.write(items);
    // Einsendungen dieses Seitenaufrufs schickt onSubmit selbst
    items.filter((i) => !this.memory.has(i.payload.id)).forEach((i) => this.send(i.payload.id));
  },
};

// Offene Einsendungen aus früheren Seitenaufrufen nachsenden
if (!window.__customFormOutboxFlushed) {
  window.__customFormOutboxFlushed = true;
  setTimeout(() => outbox.flush(), 1500);
}

const storage = {
  get(store, key) {
    try {
      return window[store].getItem(STORAGE_PREFIX + key);
    } catch {
      return null;
    }
  },
  set(store, key, value) {
    try {
      window[store].setItem(STORAGE_PREFIX + key, value);
    } catch {
      /* privater Modus o. ä. */
    }
  },
};

class CustomForm extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    try {
      this.config = JSON.parse(this.querySelector('[data-cf-config]')?.textContent || '{}');
    } catch {
      this.config = {};
    }

    this.form = this.querySelector('[data-cf-form]');
    this.panel = this.querySelector('[data-cf-panel]');
    this.success = this.querySelector('[data-cf-success]');
    this.errorBox = this.querySelector('[data-cf-error]');
    this.progress = this.querySelector('[data-cf-progress]');
    this.steps = this.buildSteps();
    this.current = 0;
    this.startedAt = Date.now();
    this.isPopup = this.config.display !== 'inline';
    this.cleanups = [];

    this.addEventListener('click', this.onClick);
    this.addEventListener('change', this.onChange);
    this.addEventListener('input', this.onInput);
    this.form?.addEventListener('submit', this.onSubmit);

    this.showStep(0);

    if (!this.isPopup) return;

    this.listen(document, 'click', this.onAnchorClick, true);
    this.listen(document, 'keydown', this.onKeydown);

    if (window.Shopify?.designMode) {
      this.setupEditor();
    } else if (this.isEligible()) {
      this.armTriggers();
    }
  }

  disconnectedCallback() {
    this.disarm();
    this.cleanups.forEach((fn) => fn());
    this.cleanups = [];
    if (openPopup === this) openPopup = null;
    this.initialized = false;
  }

  listen(target, type, handler, options) {
    target.addEventListener(type, handler, options);
    this.cleanups.push(() => target.removeEventListener(type, handler, options));
  }

  /* ---------- Häufigkeit / Berechtigung ---------- */

  get storageKey() {
    return this.config.storageKey || this.id;
  }

  isEligible() {
    const { frequency, frequencyDays, hideAfterSubmit, device } = this.config;
    if (hideAfterSubmit && storage.get('localStorage', `${this.storageKey}:done`)) return false;

    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    if (device === 'desktop' && isMobile) return false;
    if (device === 'mobile' && !isMobile) return false;

    if (frequency === 'session' && storage.get('sessionStorage', `${this.storageKey}:shown`)) return false;
    if (frequency === 'days') {
      const last = Number(storage.get('localStorage', `${this.storageKey}:shown`) || 0);
      if (last && Date.now() - last < (frequencyDays || 7) * 86400000) return false;
    }
    return true;
  }

  rememberShown() {
    storage.set('sessionStorage', `${this.storageKey}:shown`, '1');
    storage.set('localStorage', `${this.storageKey}:shown`, String(Date.now()));
  }

  /* ---------- Auslöser ---------- */

  armTriggers() {
    const t = this.config.triggers || {};
    this.timers = [];
    this.disarmers = [];
    const fire = (name) => this.open(name);

    if (t.delay != null) {
      this.timers.push(setTimeout(() => fire('Verzögerung'), t.delay * 1000));
    }

    if (t.idle != null) {
      let idleTimer;
      const reset = () => {
        clearTimeout(idleTimer);
        idleTimer = setTimeout(() => fire('Inaktivität'), t.idle * 1000);
      };
      const events = ['pointermove', 'pointerdown', 'keydown', 'scroll', 'wheel', 'touchstart'];
      // capture: Scroll-Events aus .page-wrapper (Horizon-Desktop) bubbeln nicht
      events.forEach((type) => document.addEventListener(type, reset, { passive: true, capture: true }));
      reset();
      this.disarmers.push(() => {
        clearTimeout(idleTimer);
        events.forEach((type) => document.removeEventListener(type, reset, { capture: true }));
      });
    }

    if (t.scroll != null) {
      const onScroll = () => {
        const box = getScrollContainer();
        const max = box.scrollHeight - box.clientHeight;
        if (max > 0 && (box.scrollTop / max) * 100 >= t.scroll) fire('Scrolltiefe');
      };
      document.addEventListener('scroll', onScroll, { passive: true, capture: true });
      this.disarmers.push(() => document.removeEventListener('scroll', onScroll, { capture: true }));
    }

    if (t.exit) {
      // Desktop: Maus verlässt das Fenster nach oben (Tabs/Adresszeile)
      const onMouseOut = (event) => {
        if (event.relatedTarget || event.clientY > 0) return;
        if (Date.now() - this.startedAt < 3000) return;
        fire('Verlassen der Seite');
      };
      document.addEventListener('mouseout', onMouseOut);

      // Mobil: nach etwas Lesen schnell nach oben scrollen (typisch vor „Zurück“ / Tab wechseln)
      let maxScroll = 0;
      let lastY = getScrollTop();
      let lastT = Date.now();
      const onScrollUp = () => {
        const y = getScrollTop();
        const now = Date.now();
        maxScroll = Math.max(maxScroll, y);
        const speed = (lastY - y) / Math.max(now - lastT, 1); // px pro ms, positiv = nach oben
        if (maxScroll > window.innerHeight && speed > 1.5 && now - this.startedAt > 5000) {
          fire('Verlassen der Seite');
        }
        lastY = y;
        lastT = now;
      };
      if (window.matchMedia('(pointer: coarse)').matches) {
        document.addEventListener('scroll', onScrollUp, { passive: true, capture: true });
      }
      this.disarmers.push(() => {
        document.removeEventListener('mouseout', onMouseOut);
        document.removeEventListener('scroll', onScrollUp, { capture: true });
      });
    }
  }

  disarm() {
    this.timers?.forEach(clearTimeout);
    this.disarmers?.forEach((fn) => fn());
    this.timers = [];
    this.disarmers = [];
  }

  onAnchorClick = (event) => {
    const anchor = this.config.anchor;
    if (!anchor) return;
    const trigger = event.target.closest(`a[href$="#${CSS.escape(anchor)}"], [data-custom-form-open="${CSS.escape(anchor)}"]`);
    if (!trigger) return;
    const href = trigger.getAttribute('href');
    if (href && !href.startsWith('#') && new URL(href, location.href).pathname !== location.pathname) return;
    event.preventDefault();
    this.open('Link/Button', { manual: true });
  };

  /* ---------- Öffnen / Schließen ---------- */

  open(trigger, { manual = false, editor = false } = {}) {
    if (!this.isPopup) return;
    if (!this.hidden && this.classList.contains('is-open')) return;
    if (openPopup && openPopup !== this) {
      if (!manual && !editor) return;
      openPopup.close({ silent: true });
    }

    this.disarm();
    this.openedBy = trigger;
    openPopup = this;
    this.lastFocus = document.activeElement;
    this.hidden = false;
    requestAnimationFrame(() => this.classList.add('is-open'));

    if (!editor) this.rememberShown();

    if (this.config.display === 'center') {
      document.documentElement.classList.add('custom-form-lock');
      setTimeout(() => (this.querySelector('[data-cf-focus]') || this.panel)?.focus({ preventScroll: true }), 50);
    }
  }

  close({ silent = false } = {}) {
    if (!this.isPopup || this.hidden) return;
    this.classList.remove('is-open');
    if (openPopup === this) openPopup = null;
    document.documentElement.classList.remove('custom-form-lock');
    const finish = () => {
      if (!this.classList.contains('is-open')) this.hidden = true;
    };
    silent ? finish() : setTimeout(finish, 250);
    if (this.config.display === 'center' && this.lastFocus?.focus) this.lastFocus.focus({ preventScroll: true });
  }

  toggleCollapsed() {
    const collapsed = this.classList.toggle('is-collapsed');
    this.querySelector('[data-cf-toggle]')?.setAttribute('aria-expanded', String(!collapsed));
  }

  onKeydown = (event) => {
    if (this.hidden || openPopup !== this) return;
    if (event.key === 'Escape' && this.config.display === 'center') {
      this.close();
      return;
    }
    if (event.key === 'Tab' && this.config.display === 'center') this.trapFocus(event);
  };

  trapFocus(event) {
    const focusable = [...this.panel.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')].filter(
      (el) => !el.disabled && el.offsetParent !== null
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* ---------- Theme-Editor ---------- */

  setupEditor() {
    const sectionId = this.dataset.sectionId;
    if (this.config.previewInEditor) this.open('Editor', { editor: true });

    this.listen(document, 'shopify:section:select', (event) => {
      if (event.detail?.sectionId === sectionId) this.open('Editor', { editor: true });
    });
    this.listen(document, 'shopify:section:deselect', (event) => {
      if (event.detail?.sectionId === sectionId && !this.config.previewInEditor) this.close();
    });
    this.listen(document, 'shopify:block:select', (event) => {
      if (event.detail?.sectionId !== sectionId || !this.contains(event.target)) return;
      this.open('Editor', { editor: true });
      this.classList.remove('is-collapsed');
      this.success.hidden = true;
      this.form.hidden = false;
      const step = this.steps.findIndex((s) => s.contains(event.target));
      if (step >= 0) this.showStep(step);
    });
  }

  /* ---------- Schritte ---------- */

  // Blöcke an jedem „Neuer Schritt“-Block (data-cf-step-end) in eigene Schritt-Container aufteilen
  buildSteps() {
    const container = this.querySelector('[data-cf-fields]');
    if (!container) return [];
    if (container.querySelector('[data-cf-step-group]')) return this.buildGroupSteps(container);
    const steps = [];
    const newStep = () => {
      const step = document.createElement('div');
      step.className = 'cf__step';
      step.dataset.cfStep = '';
      return step;
    };
    let step = newStep();
    [...container.children].forEach((child) => {
      step.append(child);
      if (child.matches('[data-cf-step-end]') || child.querySelector(':scope > [data-cf-step-end]')) {
        steps.push(step);
        step = newStep();
      }
    });
    if (step.children.length) steps.push(step);
    container.replaceWith(...steps);
    return steps;
  }

  /**
   * Schritte aus Blöcken „Formular: Schritt“ (Gruppen). Felder außerhalb einer Gruppe kommen in die
   * nächstgelegene Gruppe. Im letzten Schritt ersetzt die Absenden-Leiste den Weiter-Knopf.
   */
  buildGroupSteps(container) {
    // Theme-Blöcke stecken ggf. in einem Shopify-Rahmen (<div class="shopify-block">) – auspacken
    [...container.children].forEach((child) => {
      const inner = child.matches('[data-cf-step-group]') ? null : child.querySelector(':scope > [data-cf-step-group]');
      if (inner && child.children.length === 1) child.replaceWith(inner);
    });
    const children = [...container.children];
    const finalNav = children.find((child) => child.matches('.cf__nav:not([data-cf-step-nav])'));
    const steps = [];
    let loose = [];
    children.forEach((child) => {
      if (child === finalNav) return;
      if (child.matches('[data-cf-step-group]')) {
        const nav = child.querySelector(':scope > [data-cf-step-nav]');
        const first = child.firstChild || nav;
        loose.forEach((el) => child.insertBefore(el, first));
        loose = [];
        steps.push(child);
      } else {
        loose.push(child);
      }
    });
    const last = steps[steps.length - 1];
    loose.forEach((el) => last.insertBefore(el, last.querySelector(':scope > [data-cf-step-nav]')));

    // Letzter Schritt: Absenden statt Weiter, Zurück-Knopf der Gruppe übernehmen
    const lastNav = last.querySelector(':scope > [data-cf-step-nav]');
    if (finalNav) {
      const groupBack = lastNav?.querySelector('[data-cf-back]');
      const finalBack = finalNav.querySelector('[data-cf-back]');
      if (groupBack && finalBack) finalBack.textContent = groupBack.textContent;
      else if (!groupBack && finalBack && lastNav) finalBack.remove();
      lastNav?.remove();
      last.append(finalNav);
    }
    container.replaceWith(...steps);
    return steps;
  }

  showStep(index) {
    this.current = Math.max(0, Math.min(index, this.steps.length - 1));
    this.steps.forEach((step, i) => {
      step.hidden = i !== this.current;
    });
    const total = this.steps.length;
    const step = this.steps[this.current];
    step?.querySelectorAll('[data-cf-back]').forEach((btn) => {
      btn.hidden = this.current === 0;
    });
    if (this.progress) {
      this.progress.hidden = total < 2;
      this.progress.style.setProperty('--cf-progress', `${((this.current + 1) / total) * 100}%`);
      const label = this.progress.querySelector('[data-cf-progress-label]');
      if (label) label.textContent = (this.config.messages?.progress || 'Schritt {current} von {total}')
        .replace('{current}', this.current + 1)
        .replace('{total}', total);
    }
    this.setError('');
  }

  next() {
    if (!this.validateStep(this.steps[this.current])) return;
    this.showStep(this.current + 1);
    this.focusFirstField();
  }

  back() {
    this.showStep(this.current - 1);
    this.focusFirstField();
  }

  focusFirstField() {
    const field = this.steps[this.current]?.querySelector('input:not([type=hidden]), textarea, select');
    if (field && this.config.display !== 'corner') field.focus({ preventScroll: true });
  }

  /* ---------- Ereignisse ---------- */

  onClick = (event) => {
    const target = event.target;
    if (target.closest('[data-cf-close]')) {
      event.preventDefault();
      this.close();
    } else if (target.closest('[data-cf-toggle]')) {
      event.preventDefault();
      this.toggleCollapsed();
    } else if (target.closest('[data-cf-next]')) {
      event.preventDefault();
      this.next();
    } else if (target.closest('[data-cf-back]')) {
      event.preventDefault();
      this.back();
    }
  };

  onChange = (event) => {
    const field = event.target.closest('[data-cf-field]');
    if (!field) return;
    this.clearFieldError(field);

    // „Sonstiges“-Freitext ein-/ausblenden
    const other = field.querySelector('[data-cf-other-input]');
    if (other) {
      const otherChecked = field.querySelector('[data-cf-other]')?.checked ||
        field.querySelector('select')?.value === '__other__';
      other.hidden = !otherChecked;
      if (otherChecked && event.target !== other) other.focus();
    }

    // Automatisch weiter bei Einzelauswahl
    if (field.dataset.autoAdvance === 'true' && event.target.type === 'radio' && !event.target.hasAttribute('data-cf-other')) {
      const isLast = this.current === this.steps.length - 1;
      setTimeout(() => (isLast ? this.form.requestSubmit() : this.next()), 200);
    }
  };

  onInput = (event) => {
    const field = event.target.closest('[data-cf-field]');
    if (field) this.clearFieldError(field);
  };

  /* ---------- Validierung ---------- */

  fieldValue(field) {
    const type = field.dataset.type;
    if (type === 'choice' || type === 'rating') {
      const select = field.querySelector('select');
      let values = select
        ? [select.value].filter(Boolean)
        : [...field.querySelectorAll('input[type=radio]:checked, input[type=checkbox]:checked')].map((input) => input.value);
      const otherInput = field.querySelector('[data-cf-other-input]');
      values = values.map((value) => {
        if (value !== '__other__') return value;
        const text = otherInput?.value.trim();
        const label = field.dataset.otherLabel || 'Sonstiges';
        return text ? `${label}: ${text}` : label;
      });
      return values.join(', ');
    }
    if (type === 'consent') {
      return field.querySelector('input[type=checkbox]')?.checked ? this.config.messages?.yes || 'ja' : '';
    }
    return (field.querySelector('input, textarea')?.value || '').trim();
  }

  validateStep(step) {
    if (!step) return true;
    let firstInvalid = null;
    step.querySelectorAll('[data-cf-field]').forEach((field) => {
      const value = this.fieldValue(field);
      let message = '';
      if (field.dataset.required === 'true' && !value) {
        message = field.dataset.type === 'consent'
          ? this.config.messages?.consent || this.config.messages?.required
          : this.config.messages?.required;
      } else if (value && field.dataset.inputType === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        message = this.config.messages?.email;
      }
      if (message) {
        this.showFieldError(field, message);
        firstInvalid ||= field;
      } else {
        this.clearFieldError(field);
      }
    });
    if (firstInvalid) {
      firstInvalid.querySelector('input, textarea, select')?.focus({ preventScroll: true });
      firstInvalid.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      return false;
    }
    return true;
  }

  showFieldError(field, message) {
    field.classList.add('is-invalid');
    const box = field.querySelector('[data-cf-field-error]');
    if (box) {
      box.textContent = message || '';
      box.hidden = false;
    }
  }

  clearFieldError(field) {
    field.classList.remove('is-invalid');
    const box = field.querySelector('[data-cf-field-error]');
    if (box) box.hidden = true;
  }

  setError(message) {
    if (!this.errorBox) return;
    this.errorBox.textContent = message;
    this.errorBox.hidden = !message;
  }

  /* ---------- Absenden ---------- */

  onSubmit = async (event) => {
    event.preventDefault();
    if (this.submitted) return;

    // Enter in einem Feld vor dem letzten Schritt = „Weiter“
    if (this.current < this.steps.length - 1) {
      this.next();
      return;
    }
    if (!this.validateStep(this.steps[this.current])) return;

    if (!this.config.endpoint) {
      this.setError(
        window.Shopify?.designMode
          ? 'Kein Ziel hinterlegt: Bitte in den Section-Einstellungen die Google-Script-URL eintragen.'
          : this.config.messages?.error
      );
      return;
    }

    const fields = {};
    this.querySelectorAll('[data-cf-field]').forEach((field) => {
      const name = field.dataset.name;
      const value = this.fieldValue(field);
      fields[name] = fields[name] && value ? `${fields[name]}, ${value}` : fields[name] || value;
    });

    const payload = {
      id: outbox.newId(),
      form: this.config.formName,
      fields,
      meta: {},
      hp: this.form.querySelector('[data-cf-hp]')?.value || '',
      elapsed: Date.now() - this.startedAt,
    };

    // Mit Bestellnummer: erst prüfen lassen (Tags an die Bestellung), damit ein Tippfehler angezeigt werden kann
    const orderField = this.querySelector('[data-cf-field][data-type="order"]');
    const orderNumber = orderField ? this.fieldValue(orderField) : '';
    if (orderNumber) {
      payload.order = { number: orderNumber, tags: this.collectTags(orderField) };
      if (this.config.sendContext) payload.meta = await this.collectContext();
      const result = await this.verifyOrder(payload);
      if (result === 'not_found') return;
      if (result === 'saved') {
        this.submitted = true;
        this.showSuccess(fields);
        return;
      }
      // Netzwerkfehler: wie gewohnt über die Warteschlange (das Script speichert dann auch ohne Treffer)
    }

    // Sofort in die Warteschlange (localStorage), damit nichts verloren geht – auch wenn die Seite gleich verlassen wird
    const entry = outbox.add(this.config.endpoint, payload);

    // Danke sofort zeigen, gesendet wird im Hintergrund
    this.submitted = true;
    this.showSuccess(fields);

    if (this.config.sendContext && !orderNumber) {
      const meta = await this.collectContext();
      outbox.update(entry.payload.id, (item) => (item.payload.meta = meta));
    }
    outbox.send(entry.payload.id);
  };

  /** Feste Tags des Bestellnummer-Felds + Tags der gewählten Antworten („Antwort | Tag“) */
  collectTags(orderField) {
    const tags = (orderField.dataset.tags || '').split(',');
    this.querySelectorAll('input[data-tag]:checked, option[data-tag]:checked').forEach((el) => tags.push(el.dataset.tag));
    return [...new Set(tags.map((tag) => tag.trim()).filter(Boolean))];
  }

  /**
   * Sendet direkt (nicht über die Warteschlange) und wartet auf das Script.
   * @returns {Promise<'saved'|'not_found'|'offline'>}
   */
  async verifyOrder(payload) {
    const button = this.form.querySelector('[type="submit"]');
    const label = button?.innerHTML;
    if (button) {
      button.disabled = true;
      button.textContent = this.config.messages?.checking || '…';
    }
    let result = null;
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);
      const response = await fetch(this.config.endpoint, {
        method: 'POST',
        body: JSON.stringify({ ...payload, strict: true }),
        signal: controller.signal,
      });
      clearTimeout(timeout);
      result = await response.json().catch(() => null);
    } catch {
      result = null;
    }
    if (button) {
      button.disabled = false;
      button.innerHTML = label;
    }

    if (result?.ok === true) return 'saved';
    if (result?.error === 'order_not_found') {
      const field = this.querySelector('[data-cf-field][data-type="order"]');
      this.showStep(this.steps.findIndex((step) => step.contains(field)));
      this.showFieldError(field, field.dataset.notFound || this.config.messages?.error);
      field.querySelector('input')?.focus({ preventScroll: true });
      return 'not_found';
    }
    return 'offline';
  }

  showSuccess(fields) {
    if (!window.Shopify?.designMode) storage.set('localStorage', `${this.storageKey}:done`, '1');
    this.form.hidden = true;
    this.success.hidden = false;
    this.dispatchEvent(new CustomEvent('custom-form:submit', { bubbles: true, detail: { form: this.config.formName, fields } }));

    const closeAfter = this.config.successClose;
    if (this.isPopup && closeAfter > 0 && !window.Shopify?.designMode) {
      setTimeout(() => this.close(), closeAfter * 1000);
    }
  }

  async collectContext() {
    const context = this.config.context || {};
    const meta = {
      Seite: location.origin + location.pathname,
      Seitentitel: document.title,
      Gerät: window.matchMedia('(max-width: 749px)').matches ? 'Mobil' : 'Desktop',
      Sprache: document.documentElement.lang || '',
      Land: window.Shopify?.country || '',
    };
    if (context.product) meta.Produkt = context.product;
    if (this.openedBy) meta.Auslöser = this.openedBy;
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 1500);
      const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { signal: controller.signal }).then((r) => r.json());
      clearTimeout(timeout);
      meta.Warenkorb = cart.item_count
        ? `${cart.item_count} Artikel, ${(cart.total_price / 100).toFixed(2).replace('.', ',')} ${cart.currency}`
        : 'leer';
    } catch {
      /* Warenkorb optional */
    }
    return meta;
  }
}

if (!customElements.get('custom-form')) customElements.define('custom-form', CustomForm);
